<template>
    <div
        ref="target"
        class="rounded-lg shadow-xs ring-1 ring-iron-200 transition-all duration-200 ease-out hover:border-none hover:shadow-lg hover:ring-2 hover:ring-z-primary-400"
        :class="[
            expanded ? ' bg-z-primary-25 ring-2 ring-z-primary-600' : '',
            shake ? 'shake' : '',
        ]"
    >
        <form @submit.prevent="storeOrUpdate">
            <div
                class="group flex items-center justify-between gap-2 py-[10px] pl-2 pr-4"
            >
                <div class="flex items-center gap-2">
                    <button v-if="!isNew" class="cursor-move">
                        <GripVertical
                            :size="20"
                            class="field-card-handle text-iron-500"
                        />
                    </button>

                    <component
                        :is="getIconBasedOnType(props.type)"
                        class="text-iron-500"
                        :size="16"
                        :class="[isNew ? 'ml-6' : '']"
                    />
                    <p class="text-sm font-medium text-iron-700">
                        {{ localLabel }}
                    </p>
                    <p
                        class="rounded-md bg-z-primary-50 px-1 py-1 text-xs font-medium text-z-primary-600"
                    >
                        {{ "{" + localMergeTag + "}" }}
                    </p>
                </div>

                <div v-if="!expanded" class="flex items-center">
                    <button
                        type="button"
                        class="p-2"
                        @click="expanded = !expanded"
                    >
                        <Pencil
                            class="translate-y-0.5 text-iron-500 opacity-0 transition-transform duration-200 ease-out group-hover:translate-y-0 group-hover:opacity-100"
                            :size="20"
                        />
                    </button>
                </div>
                <div v-if="expanded && !isNew" class="flex items-center">
                    <button
                        v-if="mergeTag !== 'EMAIL'"
                        type="button"
                        class="group rounded-md p-2 text-destructive hover:bg-white"
                        @click="destroy"
                    >
                        <Trash
                            class="text-iron-500 group-hover:text-red-500"
                            :size="20"
                        />
                    </button>
                </div>
            </div>

            <Transition>
                <div v-if="expanded">
                    <div class="pl-9 pr-4">
                        <div class="grid w-full max-w-sm items-center gap-1.5">
                            <Label>{{
                                $t("event_types.booking_form.label")
                            }}</Label>
                            <Input v-model="localLabel" type="text" />
                        </div>
                        <div class="flex items-end gap-2">
                            <div
                                class="mt-6 grid max-w-sm items-center gap-1.5"
                            >
                                <Label>{{
                                    $t("event_types.booking_form.merge_tag")
                                }}</Label>
                                <Input
                                    v-model="localMergeTag"
                                    required
                                    type="text"
                                    :disabled="!isEditableMergeTag"
                                />
                            </div>

                            <Button
                                v-if="!isNew"
                                class="gap-2 text-iron-500"
                                variant="ghost"
                                size="sm"
                                @click="copyUuidToClipboard"
                            >
                                ID
                                <Copy v-if="!copied" :size="16" />
                                <ClipboardCopy v-if="copied" :size="16" />
                            </Button>
                        </div>
                        <div
                            class="mt-6 grid w-full max-w-sm items-center gap-1.5"
                        >
                            <div class="flex items-center space-x-2">
                                <Checkbox
                                    id="terms"
                                    :checked="
                                        localRequired || !isEditableRequired
                                    "
                                    :disabled="!isEditableRequired"
                                    @update:checked="localRequired = $event"
                                />
                                <label
                                    for="terms"
                                    class="text-sm font-medium leading-none text-iron-700 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                    {{
                                        $t("event_types.booking_form.required")
                                    }}
                                </label>
                            </div>
                        </div>

                        <div v-if="hasOptions" class="mt-6">
                            <p class="font-medium text-iron-800">
                                {{ $t("event_types.booking_form.choice") }}
                            </p>

                            <div
                                v-for="(option, i) in options"
                                :key="i"
                                class="mt-2 flex max-w-sm items-center gap-1.5"
                            >
                                <Input
                                    :model-value="option"
                                    type="text"
                                    :disabled="!isEditableMergeTag"
                                    @input="options[i] = $event.target.value"
                                />
                                <Button
                                    class="text-iron-600 hover:bg-white hover:text-red-500"
                                    variant="ghost"
                                    @click="removeOption(i)"
                                >
                                    <Trash
                                        stroke-width="1.75"
                                        :size="20"
                                    ></Trash>
                                </Button>
                            </div>

                            <Button
                                class="text-z-primary-600"
                                variant="ghost"
                                @click="addOption"
                            >
                                <Plus stroke-width="1.75" :size="20"></Plus>
                                {{ $t("event_types.booking_form.add_choice") }}
                            </Button>
                        </div>
                    </div>

                    <div
                        class="mt-4 flex justify-end gap-4 border-t border-iron-200 p-2"
                    >
                        <Button variant="outline" @click="cancel">
                            {{ $t("event_types.booking_form.cancel") }}</Button
                        >
                        <Button type="submit">
                            {{ $t("event_types.booking_form.save") }}</Button
                        >
                    </div>
                </div>
            </Transition>
        </form>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import axios from "axios";
import route from "ziggy-js";
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";
import { onClickOutside, useClipboard } from "@vueuse/core";
import {
    Type,
    PhoneCall,
    CheckSquare,
    Trash,
    AtSign,
    Plus,
    CircleDot,
    ClipboardCopy,
    Copy,
    GripVertical,
    List,
    ListChecks,
    Pencil,
} from "lucide-vue-next";

const props = defineProps<{
    name: string;
    label: string;
    mergeTag: string;
    required: boolean;
    isNew?: boolean;
    type: string;
    eventTypeId: number;
    id: number | string;
    uuid: string;
    options?: string[];
}>();

const emit = defineEmits(["remove-locally", "on-success", "on-new-added"]);

const options = ref<string[]>(props.options ? props.options : [""]);

const unedittableMergeTags = ["FIRST_NAME", "LAST_NAME", "EMAIL", "PHONE"];
const unedittableRequired = ["EMAIL"];

const target = ref();

const shake = ref(false);

const { activate, deactivate, hasFocus } = useFocusTrap(target);
const { copy, copied } = useClipboard();

onClickOutside(target, () => {
    if (hasFocus.value) {
        shake.value = true;
        setTimeout(() => {
            shake.value = false;
        }, 400);
    }
});

const hasOptions = computed(() => {
    return (
        props.type === "multiselect" ||
        props.type === "select" ||
        props.type === "radio"
    );
});

const isEditableMergeTag = computed(() => {
    return !unedittableMergeTags.includes(props.mergeTag);
});

const isEditableRequired = computed(() => {
    return !unedittableRequired.includes(props.mergeTag);
});

const localLabel = ref(props.label);
const localMergeTag = ref(props.mergeTag);
const localRequired = ref(props.required);

const expanded = ref(false);

// 'label' => 'required|string',
// 'name' => 'required|string',
// 'type' => 'required|string',
// 'required' => 'required|boolean',
// 'options' => 'nullable|array',
// 'order' => 'nullable|integer',
// 'placeholder' => 'nullable|string',

function getIconBasedOnType(type: string) {
    switch (type) {
        case "text":
            return Type;
        case "email":
            return AtSign;
        case "phone":
            return PhoneCall;
        case "number":
            return "Number";
        case "textarea":
            return Type;
        case "checkbox":
            return CheckSquare;
        case "select":
            return List;
        case "multiselect":
            return ListChecks;
        case "radio":
            return CircleDot;
        default:
            return Type;
    }
}

function cancel() {
    deactivate();
    expanded.value = false;

    if (props.isNew) {
        emit("remove-locally");
    }
}

async function storeOrUpdate() {
    if (props.isNew) {
        const res = await axios.post(
            route("app.event-types.custom-fields.store", {
                eventType: props.eventTypeId,
            }),
            {
                label: localLabel.value,
                name: localLabel.value,
                type: props.type,
                required: localRequired.value,
                mergeTag: localMergeTag.value,
                options: hasOptions.value ? options.value : undefined,
            },
        );

        const newCustomField = res.data.data;

        deactivate();
        expanded.value = false;
        emit("on-new-added", {
            newCustomField: newCustomField,
            oldUuid: props.id,
        });
    } else {
        const res = await axios.put(
            route("app.event-types.custom-fields.update", {
                eventType: props.eventTypeId,
                customField: props.id,
            }),
            {
                name: localLabel.value,
                label: localLabel.value,
                required: localRequired.value,
                mergeTag: localMergeTag.value,
                options: hasOptions.value ? options.value : undefined,
            },
        );

        deactivate();
        expanded.value = false;
    }
}

async function destroy() {
    await axios.delete(
        route("app.event-types.custom-fields.delete", {
            eventType: props.eventTypeId,
            customField: props.id,
        }),
    );

    deactivate();
    expanded.value = false;
    emit("remove-locally");
}

function copyUuidToClipboard() {
    copy(props.uuid);
}

function addOption() {
    options.value.push("");
}

function removeOption(index: number) {
    options.value = options.value.filter((_, i) => i !== index);
}

watch(expanded, () => {
    if (expanded.value) {
        console.log(target.value);

        console.log("test");

        nextTick(() => {
            activate();
        });
    }
});

onMounted(() => {
    if (props.isNew) {
        expanded.value = true;

        activate();
    }
});
</script>

<style scoped>
.shake {
    animation: shake 0.4s;

    animation-iteration-count: 1;
}

@keyframes shake {
    0% {
        transform: translate(1px, 1px) rotate(0deg);
    }
    10% {
        transform: translate(-1px, -2px) rotate(-1deg);
    }
    20% {
        transform: translate(-3px, 0px) rotate(1deg);
    }
    30% {
        transform: translate(3px, 2px) rotate(0deg);
    }
    40% {
        transform: translate(1px, -1px) rotate(1deg);
    }
    50% {
        transform: translate(-1px, 2px) rotate(-1deg);
    }
    60% {
        transform: translate(-3px, 1px) rotate(0deg);
    }
    70% {
        transform: translate(3px, 1px) rotate(-1deg);
    }
    80% {
        transform: translate(-1px, -1px) rotate(1deg);
    }
    90% {
        transform: translate(1px, 2px) rotate(0deg);
    }
    100% {
        transform: translate(1px, -2px) rotate(-1deg);
    }
}
</style>
